import { useMemo } from 'react';
import { loggerService } from 'log';
import { action } from 'js/hooks/auth/pas';
import {
    useCheckUsernameAvailability,
    useGenericSend,
    useGetURLTemplate,
    useGetURLTemplates
} from 'server';

import * as hooks from './hooks';
import * as server from './server';

export const useServerPublicAPI = () => {
    const send = useGenericSend();
    const getURLTemplate = useGetURLTemplate();
    const getURLTemplates = useGetURLTemplates();
    const registerPushApplication = hooks.useRegisterPushApplication();
    const checkIfUsernameIsAvailable = useCheckUsernameAvailability();
    const checkInDotComListenerRequest = hooks.useCheckInDotComListenerRequest();

    return useMemo(() => ({
        /**
         * Retrieve URL templates from IMS. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/GetURLsNew.html}
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {string[]} options.urlTypeList
         * @param {string} [options.casinoName=casinoName from CMS]
         * @param {string} [options.clientSkin=clientSkin from CMS]
         * @param {string} [options.clientPlatform=clientPlatform from device settings]
         * @param {string} [options.clientType=clientType from CMS]
         * @param {string} [options.language=user language]
         * @param {string} [options.creferer=creferer from user details]
         * @returns {Promise<{data: {urlList: Array<{priority: number, url: string, urlType: string}>}}>}
         * @example
         * // {data:{urlList:[{urlType:"cashier",url:"https://core12.playtechgaming.com/quick-deposit?back_url=[back_url]",priority:1}],correlationId:"correlationId_24797775-56d7-4a92-881f-4a613e92537f"},ID:35845}
         * Playtech.API.server.getURLTemplates({ urlTypeList: ['cashier'] }).then((data) => { ... });
         */
        getURLTemplates: async (options) => {
            const data = await getURLTemplates(options);

            return { data };
        },
        /**
         * Retrieve URL template url with the highest priority from IMS. If "priority" parameter is passed, url with specified priority will be returned.
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {string[]} options.urlTypeList
         * @param {number} [options.priority=priority of the specific url template]
         * @param {string} [options.casinoName=casinoName from CMS]
         * @param {string} [options.clientSkin=clientSkin from CMS]
         * @param {string} [options.clientPlatform=clientPlatform from device settings]
         * @param {string} [options.clientType=clientType from CMS]
         * @param {string} [options.language=user language]
         * @param {string} [options.creferer=creferer from user details]
         * @returns {Promise<string | undefined>}
         * @example
         * Playtech.API.server.getURLTemplate({ urlTypeList: ['cashier'], priority: 1 }).then((url) => { ... });
         */
        getURLTemplate,
        /**
         * Used to initiate Document upload for logged-in users. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/InitiateDocumentUpload.html}
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {string} options.successUrl
         * @param {string} options.errorUrl
         * @param {string} [options.service = 'netverify']
         * @param {string} [options.documentType]
         * @param {string} [options.remotePaymentAccountId]
         * @returns {Promise<{data: {redirectURL: string, reference: string}}>}
         * @example
         * Playtech.API.server.initiateDocumentUpload({
         *      errorUrl: 'https://error.com',
         *      documentType: 'PASSPORT',
         *      successUrl: 'https://success.com',
         *      remotePaymentAccountId: 'ePIl8ceR',
         * });
         */
        initiateDocumentUpload: server.initiateDocumentUpload,
        /**
         * Used to assign player personal device/browser. This device can later be used to push notifications. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/RegisterPushApplication.html}
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {string} options.application i.e. casino_mobile_2017.
         * @param {string} [options.deviceId] Default value is taken from stored device settings.
         * @param {string} [options.pushId ]
         * @param {string} [options.deviceType] Values: 'ios', 'android', 'chrome', 'firefox'.
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.server.registerPushApplication({
         *      application: 'TestApp',
         *      deviceId: 'asdf2dvaxdf',
         *      pushId: 'asdfasdrfqw34asdfzxcvq345dfgGDSFSDfasdfASDfasdfSDF',
         *      deviceType: 'android'
         * }).then(() => { ... });
         */
        registerPushApplication,
        /**
         * Check if player's userName is available. If not, return also list of suggested userNames. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/CheckUsernameAvailability.html}
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {string} options.userName i.e. testplayer.
         * @param {string} options.email i.e. test@playtech.com.
         * @param {string} options.phone i.e. +1-555-55-55
         * @param {string} options.country i.e. Ukraine
         * @param {string} [options.token] external verification token, e.g. from ReCaptcha.
         * @returns {Promise<{usernameAvailable: boolean, phoneAvailable: boolean, emailAvailable: string, suggestions: Array<string>}>} emailAvailable values: 'available', 'unavailable', 'unknown'
         * @example
         * Playtech.API.server.checkIfUsernameIsAvailable({
         *      userName: 'testplayer'
         * }).then((data) => { ... });
         */
        checkIfUsernameIsAvailable,
        /**
         * See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/CheckInDotComListener.html" target="_blank}
         * @memberOf Playtech.API.server
         * @function
         * @param {Object} options
         * @param {Object[]} options.getParameters get parameters of the request.
         * @param {string} options.getParameters[].key
         * @param {string} options.getParameters[].value
         * @param {string} options.requestContent base64 content of the data received from CheckIn.com.
         * @returns {Promise<{ actions: Object }>}
         * @example
         * Playtech.API.server.checkInDotComListenerRequest({
         *     getParameters: [{
         *         key: "pinNumber",
         *         value: "7924"
         *     }],
         *     "requestContent":"ew0KI..."
         * }).then((data) => { ... });
         */
        checkInDotComListenerRequest,
        /**
         * Request for get JWT token for Zendesk integration. ServerUrl - url to chat-admin server.
         * @memberOf Playtech.API.server
         * @function
         * @param {string} serverUrl
         * @returns {Promise<{data: {jwtToken: string}}>}
         * @example
         * Playtech.API.server.getJWTToken('https://www.example.com').then((data) => { ... });
         */
        getJWTToken: server.getJWTToken,
        ...loggerService.isEnabled() ?
            {
                /**
                 * Hidden and only available in debug mode. Returns send function.
                 * @memberOf Playtech.API.server
                 * @function
                 * @param {string} ID
                 * @returns {Promise<{Object}>} Depends on request
                 * @example
                 * Playtech.API.server.send({
                 *      ID: 72057,
                 *      description: 'Get JWT Token request',
                 *      responses: [72058, 72059],
                 *      serverUrl: 'https://www.example.com'
                 * }).then((data) => { ... });
                 */
                send,
                /**
                 * Call PAS action by name
                 * Hidden and only available in debug mode. Returns send to pas
                 * @memberOf Playtech.API.server
                 * @function
                 * @param {string} actionName
                 * @param {string} callbackName
                 * @param {Array} args
                 * @returns {Promise<{Object}>}
                 * @example
                 * Playtech.API.server.pasSend('iapiEmailLoginAndGetTempToken', 'iapiCALLOUT_LOGINANDGETTEMPTOKEN', [ 'userName', 'password', 1, 'languageCode']).then((data) => { ... });
                 */
                pasSend: action
            } : {}
    }), [
        send,
        getURLTemplate,
        getURLTemplates,
        registerPushApplication,
        checkIfUsernameIsAvailable,
        checkInDotComListenerRequest
    ]);
};
